import React, { useState, useEffect, useRef, useCallback } from "react";
import { Animated, View, Text, Pressable } from "react-native";
import { NotificationProps } from "./Notification.types";
import styles from "./Notification.styles";

// Icônes pour chaque type
const getIcon = (type: string) => {
  switch (type) {
    case 'success': return '✓';
    case 'error': return '✕';
    case 'warning': return '⚠';
    case 'info': 
    default: return 'ℹ';
  }
};

export const Notification: React.FC<NotificationProps> = ({
  message,
  type = "info",
  duration = 3000,
  position = "bottom",
  showCloseButton = true,
  onHide,
}) => {
  const [visible, setVisible] = useState(true);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(position === 'top' ? -100 : 100)).current;

  const handleHide = useCallback(() => {
    const slideDirection = position === 'top' ? -100 : 100;
    
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: slideDirection,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setVisible(false);
      onHide?.();
    });
  }, [fadeAnim, slideAnim, position, onHide]);

  useEffect(() => {
    // Animation d'apparition
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        tension: 100,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Disparition automatique si duration > 0
    let timer: NodeJS.Timeout;
    if (duration > 0) {
      timer = setTimeout(() => {
        handleHide();
      }, duration);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [fadeAnim, slideAnim, duration, handleHide]);

  if (!visible) return null;

  // Gestion sécurisée des styles
  const getPositionStyle = () => {
    switch (position) {
      case 'top': return styles.positionTop;
      case 'center': return styles.positionCenter;
      case 'bottom':
      default: return styles.positionBottom;
    }
  };

  const getTypeStyle = () => {
    switch (type) {
      case 'success': return styles.success;
      case 'error': return styles.error;
      case 'warning': return styles.warning;
      case 'info':
      default: return styles.info;
    }
  };

  return (
    <Animated.View 
      style={[
        styles.container, 
        getPositionStyle(),
        getTypeStyle(),
        { 
          opacity: fadeAnim,
          transform: [{ 
            translateY: slideAnim 
          }],
        }
      ]}
      pointerEvents="auto"
    >
      <View style={styles.contentContainer}>
        <View style={styles.iconContainer}>
          <Text style={styles.closeButtonText}>{getIcon(type)}</Text>
        </View>
        <View style={styles.textContainer}>
          <Text style={styles.text}>{message}</Text>
        </View>
      </View>
      
      {showCloseButton && (
        <Pressable style={styles.closeButton} onPress={handleHide}>
          <Text style={styles.closeButtonText}>×</Text>
        </Pressable>
      )}
    </Animated.View>
  );
};
